import React, { useCallback, useMemo } from 'react';
import { InteractiveTooltipProps, tooltipDefaults } from './domain';
import { Box, ClickAwayListener, Tooltip, tooltipClasses } from '@mui/material';

const InteractiveTooltip: React.FC<InteractiveTooltipProps> = ({
  Trigger,
  Contents,
  ...tooltipConfigs
}) => {
  const {
    overrideIsOpen,
    isWithArrow,
    tooltipDelay = tooltipDefaults.tooltipDelay,
    isThroughPortal = tooltipDefaults.isThroughPortal,
    targetPlacement = tooltipDefaults.targetPlacement,
    componentsSx = tooltipDefaults.componentsSx,
    tooltipOffsets = tooltipDefaults.tooltipOffsets,
    tooltipPreventOverflow = tooltipDefaults.tooltipPreventOverflow,
    setOverrideIsOpen,
  } = tooltipConfigs;

  const isWithOverrideOpenState = Boolean(overrideIsOpen !== undefined);

  const formattedTooltipDelay = useMemo(() => {
    if (typeof tooltipDelay === 'number') {
      return {
        enter: tooltipDelay,
        leave: tooltipDelay,
      };
    }
    return tooltipDelay;
  }, [tooltipDelay]);

  const onClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (isWithOverrideOpenState && setOverrideIsOpen) {
        setOverrideIsOpen(false);
      }
    },
    [isWithOverrideOpenState, setOverrideIsOpen],
  );

  if (!Trigger) return null;
  if (!Contents) return Trigger;

  return (
    <Tooltip
      arrow={isWithArrow}
      open={overrideIsOpen}
      title={
        <ClickAwayListener onClickAway={onClickAway}>
          <Box component="span">{Contents}</Box>
        </ClickAwayListener>
      }
      PopperProps={{
        disablePortal: !isThroughPortal,
        onMouseDown: (event) => {
          event.stopPropagation();
          event.nativeEvent.stopPropagation();
        },
        onMouseUp: (event) => {
          event.stopPropagation();
          event.nativeEvent.stopPropagation();
        },
        modifiers: [
          {
            // offsets
            // https://popper.js.org/docs/v2/modifiers/offset
            name: 'offset',
            options: {
              offset: [tooltipOffsets?.x ?? 0, tooltipOffsets?.y ?? 0],
            },
          },
          {
            // preventOverflow
            // https://popper.js.org/docs/v2/modifiers/prevent-overflow/
            name: 'preventOverflow',
            enabled: true,
            options: {
              padding: tooltipPreventOverflow,
              altAxis: true,
            },
          },
          {
            // flip
            // https://popper.js.org/docs/v2/modifiers/flip/
            name: 'flip',
            enabled: true,
            options: {
              padding: 8,
              rootBoundary: 'document',
              altBoundary: true,
            },
          },
        ],
      }}
      placement={targetPlacement}
      slotProps={{
        popper: {
          sx: {
            ...(componentsSx?.root ?? {}),
          },
        },
        tooltip: {
          sx: {
            background: 'transparent',
            padding: 0,
            maxWidth: 'unset',
            ...(componentsSx?.tooltip ?? {}),
          },
        },
        arrow: {
          sx: {
            marginBottom: '-0.625em',
            ...(componentsSx?.arrow ?? {}),
          },
        },
      }}
      enterDelay={formattedTooltipDelay?.enter}
      enterNextDelay={formattedTooltipDelay?.enter}
      leaveDelay={formattedTooltipDelay?.leave}
    >
      {Trigger}
    </Tooltip>
  );
};

export default InteractiveTooltip;
